<template>
  <v-row class="ma-0 pa-0 chart-wrapper" align="center" justify="center">
    <div class="chart-actions">
      <v-btn-toggle
        v-model="selected_reach_axis"
        class="chart-axis-toggle chip-style-toggle pr-6"
        mandatory
        @change="setup_chart_config()"
      >
        <v-btn class="chip-style-toggle__btn" small value="reach_pct">Reach %</v-btn>
        <v-btn class="chip-style-toggle__btn" small value="reach_pop">Reach Pop</v-btn>
      </v-btn-toggle>
      <v-menu :close-on-content-click="false">
        <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
          <v-tooltip top>
            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
              <v-btn
                class="chart-action-btn"
                icon
                v-bind="{ ...menuAttrs, ...tooltipAttrs }"
                v-on="{ ...menuOn, ...tooltipOn }"
              >
                <v-icon color="black">mdi-chart-areaspline-variant</v-icon>
              </v-btn>
            </template>
            <TooltipDescription
              title="Set chart dimension"
              desc_2=""
            ></TooltipDescription>
          </v-tooltip>
        </template>

        <v-card class="ma-0 py-4 px-2">
          <v-row class="ma-0 pa-0">
            <v-col cols="12" class="ma-0 pa-0">
              <v-select
                class="ma-0"
                v-model="selected_dimension"
                label="Dimension"
                :items="[
                  { label: 'Spend', value: 'spend' },
                  { label: 'Impressions', value: 'impressions' },
                  { label: 'GRPs', value: 'grps' },
                  { label: 'Daily GRPs', value: 'daily_grps' }
                ]"
                item-text="label"
                item-value="value"
                hide-details
                return-object
                @change="setup_chart_config()"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-menu>
    </div>
    <v-col cols="12" class="chart_style">
      <v-chart
        :id="chart_id"
        :ref="chart_ref"
        class="chart"
        :option="chartOption"
        autoresize
        @click="handlePointClick"
        @legendselectchanged="handleLegendSelectChanged"
        :key="chart_key"
      ></v-chart>
    </v-col>
  </v-row>
</template>

<script>
import VChart, { THEME_KEY } from "vue-echarts";
import "echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { TitleComponent, TooltipComponent, LegendComponent } from "echarts/components";
import TooltipDescription from "@/components/Utility/TooltipDescription.vue";
import cpalette from "@/configs/ChartColors/get_color_palette";

use([CanvasRenderer, TitleComponent, TooltipComponent, LegendComponent]);
export default {
  name: "SweetSpotReachChart",
  props: [
    "chart_title",
    "chart_data",
    "chart_id",
    "chart_ref",
    "chart_options",
    "color_palette",
    "shared_legend_selected",
  ],
  components: {
    VChart,
    TooltipDescription,
  },
  provide: {
    [THEME_KEY]: "light",
  },
  data() {
    return {
      chart_key: 0,
      selected_dimension: { label: 'Spend', value: 'spend' },
      selected_reach_axis: "reach_pct",
      legend_selected: {},
      chartOption: {},
    };
  },
  mounted() {
    this.setup_chart_config();
  },
  watch: {
    shared_legend_selected: {
      deep: true,
      handler(selected) {
        this.legend_selected = { ...this.legend_selected, ...selected };
        if (this.chartOption.legend) {
          this.chartOption = {
            ...this.chartOption,
            legend: { ...this.chartOption.legend, selected: { ...this.legend_selected } },
          };
        }
      },
    },
    chart_data: {
      deep: true,
      handler() {
        this.setup_chart_config();
        this.chart_key += 1;
      },
    },
  },

  methods: {
    setup_chart_config() {
      const datasetWithFilters = [];
      const dimensionLabelMap = {
        spend: "Spend",
        impressions: "Impressions",
        grps: "GRPs",
        daily_grps: "Daily GRPs",
      };
      const reachAxisConfig = {
        reach_pct: {
          label: "Unique Reach %",
          sourceLabel: "Reach",
        },
        reach_pop: {
          label: "Reach Population",
          sourceLabel: "Reach Population",
        },
      };
      const selectedReachAxis = reachAxisConfig[this.selected_reach_axis];

      const marker_opts_tmp = [];
      const formatCompactNumber = (value, fractionDigits) => {
        return new Intl.NumberFormat("en-US", {
          notation: "compact",
          compactDisplay: "short",
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }).format(value);
      };
      const formatAxisValue = (label, value) => {
        const numericValue = Number(value);
        if (value == null || value === "" || Number.isNaN(numericValue)) {
          return "N/A";
        }
        if (label === "Spend") {
          return `$${formatCompactNumber(numericValue, 2)}`;
        }
        if (label === "Impressions") {
          return formatCompactNumber(numericValue, 0);
        }
        if (label === "GRPs" || label === "Daily GRPs") {
          return formatCompactNumber(numericValue, 2);
        }
        return numericValue.toLocaleString();
      };
      const formatTooltipValue = (label, value) => {
        const numericValue = Number(value);
        if (value == null || value === "" || Number.isNaN(numericValue)) {
          return `${label}: N/A`;
        }
        if (label === "Spend") {
          return `${label}: $${formatCompactNumber(numericValue, 2)}`;
        }
        if (label === "Unique Reach") {
          return `${label}: ${(numericValue * 100).toFixed(1)}%`;
        }
        if (label === "Reach Population") {
          return `${label}: ${formatCompactNumber(numericValue, 0)}`;
        }
        if(label === "Impressions") {
          return `${formatCompactNumber(numericValue, 0)}`;
        }
        if (label === "GRPs" || label === "Daily GRPs") {
          return `${label}: ${formatCompactNumber(numericValue, 2)}`;
        }
        return `${label}: ${numericValue.toLocaleString()}`;
      };

      const generateSeriesList = () => {
        const seriesList = [];
        this.$props.chart_data.forEach((item) => {
          const header_row = item["reach_curve"][0];
          const dimensionLabel = dimensionLabelMap[this.selected_dimension.value];
          const dimension_idx = header_row.indexOf(dimensionLabel);
          const reach_idx = header_row.indexOf("Reach");
          const reach_pop_idx = header_row.indexOf("Reach Population");
          const selected_reach_idx = header_row.indexOf(selectedReachAxis.sourceLabel);
          const spend_idx = header_row.indexOf("Spend");
          const impressions_idx = header_row.indexOf("Impressions");
          const grps_idx = header_row.indexOf("GRPs");
          const daily_grps_idx = header_row.indexOf("Daily GRPs");
          const reach_curve = item["reach_curve"].slice(1).map((point) => ({
            value: [
              point[dimension_idx],
              point[selected_reach_idx],
              point[spend_idx],
              point[impressions_idx],
              point[grps_idx],
              point[daily_grps_idx],
              point[reach_idx],
              point[reach_pop_idx],
            ],
          }));

          const marker_idx = item["marker_idx"];
          marker_opts_tmp.push({
            plan_id: item["plan_id"],
            plan_name: item["plan_name"],
          });
          
          let point = reach_curve[marker_idx - 1]?.value || [0, 0];

          let mark_line = [
              [{ coord: [point[0], point[1]] }, { coord: [point[0], 0] }],
              [{ coord: [point[0], point[1]] }, { coord: [0, point[1]] }],
            ];

          seriesList.push({
            type: "line",
            markLine: {
              data: mark_line,
              animation: false,
              lineStyle: {
                width: 2,
                type: "dashed",
              },
            },
            smooth: true,
            data: reach_curve,
            showSymbol: false,
            id: item["plan_id"],
            name: item["plan_name"],
            endLabel: {
              show: false,
              formatter: function (params) {
                return params.value[3] + ": " + params.value[0];
              },
            },
            labelLayout: {
              moveOverlap: "shiftY",
            },
            emphasis: {
              focus: "series",
            },
            encode: {
              x: 0,
              y: "Reach",
              label: ["Face ID", "Reach"],
              itemName: "Date",
              tooltip: ["Reach"],
            },
          });
        });
        return seriesList;
      };

      const series_list = generateSeriesList();
      const legendList = marker_opts_tmp.map((item) => item["plan_name"]);
      const legendSelected = {};

      const selections = { ...this.legend_selected, ...this.shared_legend_selected };
      legendList.forEach((name) => {
        legendSelected[name] =
          selections[name] === undefined
            ? true
            : selections[name];
      });
      this.legend_selected = legendSelected;

      let chartOption = {
        animationDuration: 500,
        color: cpalette.get_color_palette(this.$props.color_palette),
        dataset: [
          {
            id: "dataset_raw",
            source: this.$props.chart_data,
          },
          ...datasetWithFilters,
        ],
        title: {
          text: this.$props.chart_title,
          fontFamily: "Poppins",
          left: "center",
        },
        legend: {
          data: legendList,
          selected: legendSelected,
          type: "scroll",
          top: "5%",
          left: "center",
          textStyle: {
            fontFamily: "Poppins",
            fontSize: 12,
          },
        },
        tooltip: {
          order: "valueAsc",
          trigger: "axis",
          
          
          textStyle: {
            fontFamily: "Poppins",
          },
          formatter: function (params) {
            const body = params
              .map((item) => {
                const rows = [
                  ["Spend", formatTooltipValue("Spend", item.value[2]).replace("Spend: ", "")],
                  ["Impressions", formatTooltipValue("Impressions", item.value[3]).replace("Impressions: ", "")],
                  ["GRPs", formatTooltipValue("GRPs", item.value[4]).replace("GRPs: ", "")],
                  ["Daily GRPs", formatTooltipValue("Daily GRPs", item.value[5]).replace("Daily GRPs: ", "")],
                  ["Unique Reach", formatTooltipValue("Unique Reach", item.value[6]).replace("Unique Reach: ", "")],
                  ["Reach Population", formatTooltipValue("Reach Population", item.value[7]).replace("Reach Population: ", "")],
                ];

                const rowsHtml = rows
                  .map(([label, value]) => {
                    return `<div style="display:flex; justify-content:space-between; gap:4px; font-family:Poppins;"><span style="font-weight:600;"">${label}</span><span style="text-align:right; min-width:90px;">${value}</span></div>`;
                  })
                  .join("");

                return `<div style="font-family:Poppins;"><div style="font-weight:800; margin-bottom:6px;">${item.seriesName || ""}</div>${rowsHtml}</div>`;
              })
              .join("<br/><br/>");

            return body;
          },
        },
        xAxis: {
          axisLabel: {
            formatter: (value) => {
              return formatAxisValue(this.selected_dimension["label"], value);
            },
            fontSize: 12,
            fontFamily: "Poppins",
          },
          splitLine: {
            show: false,
          },
          name: this.selected_dimension['label'],
          nameLocation: "middle",
          nameGap: 28,
          nameTextStyle: {
            fontSize: 14,
            fontFamily: "Poppins",
            fontWeight: "bold",
          },
        },
        yAxis: {
          axisLabel: {
            formatter: (value) => {
              if (this.selected_reach_axis === "reach_pct") {
                return (value * 100).toFixed(0) + "%";
              }
              return formatCompactNumber(value, 0);
            },
            fontSize: 12,
            fontFamily: "Poppins",
          },
          splitLine: {
            show: false,
          },
          name: selectedReachAxis.label,
          nameLocation: "middle",
          nameGap: 40,
          nameTextStyle: {
            fontSize: 14,
            fontFamily: "Poppins",
            fontWeight: "bold",
          },
        },
        grid: {
          show: false,
          right: "3%",
          left: "3%",
          bottom: 96,
          top: "12%"
        },
        dataZoom: [
          {
            type: "slider",
            xAxisIndex: 0,
            start: 0,
            end: 100,
            bottom: 8,
            height: 32,
          },
          // {
          //   type: "inside",
          //   xAxisIndex: 0,
          //   start: 0,
          //   end: 100,
          // },
        ],
        series: series_list,
      };
      this.chartOption = chartOption;
    },
    handlePointClick(params) {
      const marker_value = params.value;

      const marker_payload = {
        plan_id: params.seriesId,
        attribute: this.selected_dimension["value"],
        selected_idx: params.dataIndex
      };

      this.chartOption.series.forEach((series) => {
        if (series.id == params.seriesId) {
          const updated_mark_line = [
            [
              { coord: [marker_value[0], marker_value[1]] },
              { coord: [marker_value[0], 0] },
            ],
            [
              { coord: [marker_value[0], marker_value[1]] },
              { coord: [0, marker_value[1]] },
            ],
          ];
          series.markLine.data = updated_mark_line;
        }
      });
      this.chart_key += 1; // Force re-render

      this.$emit("update_data_from_marker", marker_payload);
    },
    handleLegendSelectChanged(params) {
      this.legend_selected = { ...(params.selected || {}) };
      this.$emit("legend_selection_changed", this.legend_selected);
    },
  },
};
</script>

<style scoped>
.chart-wrapper {
  position: relative;
}

.chart-actions {
  position: absolute;
  top: 0px;
  right: 8px;
  display: flex;
  gap: 4px;
  width: auto;
  z-index: 2;
  pointer-events: none;
}

.chart-action-btn {
  pointer-events: auto;
}

.chart-axis-toggle {
  pointer-events: auto;
  height: 36px;
}

.chip-style-toggle {
  background: transparent !important;
  border-radius: 0;
  box-shadow: none;
  gap: 2px;
}

.chip-style-toggle ::v-deep .chip-style-toggle__btn {
  background-color: #ffffff !important;
  border: 2px solid #000000 !important;
  border-radius: 0 !important;
  color: #000000 !important;
  font-family: Poppins;
  font-weight: 600;
  height: 36px !important;
  justify-content: center;
  margin-left: 1px;
  margin-right: 1px;
  min-width: 125px;
  padding: 0 12px !important;
  text-transform: none;
}

.chip-style-toggle ::v-deep .chip-style-toggle__btn.v-btn--active {
  background-color: #000000 !important;
  color: #ffffff !important;
}

.chip-style-toggle ::v-deep .chip-style-toggle__btn::before {
  display: none;
}

.chart_title {
  font-family: "Poppins" !important;
  font-size: 10pt !important;
  line-height: 40px;

  display: block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chart_style {
  height: 65vh;
  font-family: "Poppins" !important;
}
</style>
